import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type Router from "cc-frontend/router"
import type Rpc from "cc-frontend/services/rpc"

export default class AdminCreateSchool extends Component {
  @service declare rpc: Rpc
  @service declare router: Router

  @tracked title = ""
  @tracked hubspotCompanyUrl = ""
  @tracked errorMessage: string | null = null
  @tracked isCreating = false

  get parsedHubspotCompanyId(): string | null {
    return parseHubspotCompanyId(this.hubspotCompanyUrl)
  }

  @action
  setTitle(event: Event) {
    this.title = (event.target as HTMLInputElement).value
  }

  @action
  setHubspotCompanyUrl(event: Event) {
    this.hubspotCompanyUrl = (event.target as HTMLInputElement).value
  }

  @action
  async createSchool(event: Event) {
    event.preventDefault()
    if (this.isCreating) return

    const title = this.title.trim()
    if (title === "") {
      this.errorMessage = "Title is required."
      return
    }

    let hubspotCompanyId: string | undefined = undefined
    if (this.hubspotCompanyUrl.trim() !== "") {
      const parsed = parseHubspotCompanyId(this.hubspotCompanyUrl)
      if (parsed === null) {
        this.errorMessage =
          "Could not find a company id in that Hubspot URL. It should look like https://app.hubspot.com/contacts/40639032/record/0-2/34477673035"
        return
      }
      hubspotCompanyId = parsed
    }

    this.errorMessage = null
    this.isCreating = true
    try {
      const resp = await this.rpc.group_create_school_v2({ title, hubspotCompanyId })
      if (resp.responseOk) {
        this.router.transitionTo("admin.group", resp.result.groupId)
      } else {
        this.errorMessage = resp.result.message ?? "Creating the school failed."
      }
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : String(error)
    } finally {
      if (!this.isDestroyed && !this.isDestroying) this.isCreating = false
    }
  }
}

// Company records live under object type "0-2" in Hubspot URLs, e.g.
// https://app.hubspot.com/contacts/40639032/record/0-2/34477673035?eschref=...
const COMPANY_ID_IN_PATH = /\/0-2\/(\d+)/

/**
 * Pulls the company id out of a pasted Hubspot company URL, or passes through a
 * bare id. Returns null if neither shape matches.
 */
function parseHubspotCompanyId(input: string): string | null {
  const trimmed = input.trim()
  if (trimmed === "") return null
  if (/^\d+$/.test(trimmed)) return trimmed

  // The eschref query param embeds its own (url-encoded) "0-2" path, so only the
  // part before the query string can be trusted.
  const match = COMPANY_ID_IN_PATH.exec(trimmed.split("?")[0])
  return match === null ? null : match[1]
}
